import Bits from "../../utils/bits";
import { bindActionCreators } from "redux";
import Device, { observeColorScheme } from "../../init/device";
import { isFunction, isPlainObject, withProperties } from "../../utils";
import { UPDATE_DARK_MODE, UPDATE_SYSTEM_COLOR_OVERRIDE } from "../helpers/actions";

const DARK_MODE_FLAG = 1;
const SYSTEM_OVERRIDE_FLAG = 2;

const STATE = {
  darkMode: false,
  overrideSystem: false
};

let $unobserve;
let COLOR_SCHEME_PREFERENCE = 0; // (local variable preference)

export default function state() {
  const $state = Object.create(null);
  const NAME = 'color';
  const { dispatch } = this;

  function Reducer(state = STATE, action) {
    const payload = isPlainObject(action.payload) ? action.payload : {};

    switch (action.type) {
      case UPDATE_DARK_MODE: {
        let { dark } = payload;
        const { darkMode } = state;

        return (dark = dark === true) !== darkMode
          ? { ...state, darkMode: dark }
          : state;
      }

      case UPDATE_SYSTEM_COLOR_OVERRIDE: {
        let { override } = payload;
        const { overrideSystem } = state;

        if ((override = override === true) !== overrideSystem) {
          return {
            darkMode: override ? __getDarkModePreference__() : Device.darkMode,
            overrideSystem: override,
          };
        }

        return state;
      }

      default: return state;
    }
  }

  function __getPreference__() {
    // Could be from localStorage or API data (using local variable, for now)
    return Bits.truncate(COLOR_SCHEME_PREFERENCE, 2);
  }

  function __setPreference__(bits) {
    // Could be set on localStorage or via API call (using local variable, for now)
    COLOR_SCHEME_PREFERENCE = Bits.truncate(bits, 2);
  }

  function __getDarkModePreference__() {
    return !!(__getPreference__() & DARK_MODE_FLAG);
  }

  function __getSystemOverridePreference__() {
    return !!(__getPreference__() & SYSTEM_OVERRIDE_FLAG);
  }

  function __setupColorSchemeMonitor__() {
    return (dispatch) => {
      const { overrideSystem } = $state.STATE;

      if (overrideSystem) {
        isFunction($unobserve) && $unobserve(() => {
          $unobserve = undefined;
        });
      } else if (!isFunction($unobserve)) {
        $unobserve = observeColorScheme(currentColorScheme => {
          const dark = currentColorScheme === 'dark';
          dispatch({ type: UPDATE_DARK_MODE, payload: { dark } });
        });
      }
    }
  }

  function updateDarkMode(dark) {
    return (dispatch) => {
      const { darkMode, overrideSystem } = $state.STATE;

      if (overrideSystem && (dark = dark === true) !== darkMode) {
        dispatch({ type: UPDATE_DARK_MODE, payload: { dark } });
        __setPreference__(__getPreference__() ^ DARK_MODE_FLAG);
      }
    }
  }

  function updateOverrideSystem(override) {
    return (dispatch) => {
      const { overrideSystem } = $state.STATE;

      if ((override = override === true) !== overrideSystem) {
        dispatch({ type: UPDATE_SYSTEM_COLOR_OVERRIDE, payload: { override } });
        __setPreference__(__getPreference__() ^ SYSTEM_OVERRIDE_FLAG);
        dispatch(__setupColorSchemeMonitor__());
      }
    }
  }

  function turnOnDarkMode() {
    return (dispatch) => {
      dispatch(updateDarkMode(true));
    }
  }

  function turnOffDarkMode() {
    return (dispatch) => {
      dispatch(updateDarkMode(false));
    }
  }

  function overrideSystemColorMode() {
    return (dispatch) => {
      dispatch(updateOverrideSystem(true));
    }
  }

  function useSystemColorMode() {
    return (dispatch) => {
      dispatch(updateOverrideSystem(false));
    }
  }

  // Augment $state with properties
  withProperties($state, {
    NAME,
    Reducer,
    actions: withProperties(Object.create(null), { updateDarkMode, updateOverrideSystem }),
    exposedActions: withProperties(Object.create(null), {
      AutoColorMode: bindActionCreators({ on: useSystemColorMode, off: overrideSystemColorMode }, dispatch),
      DarkMode: bindActionCreators({ on: turnOnDarkMode, off: turnOffDarkMode }, dispatch)
    })
  });

  // Initially, $state will be an empty object, because the setup function
  // has not been called. Using setTimeout() here ensures that the dispatch
  // action happens on the next tick of the event loop.
  setTimeout(() => {
    const override = __getSystemOverridePreference__();

    dispatch({
      type: UPDATE_SYSTEM_COLOR_OVERRIDE,
      payload: { override }
    });

    dispatch({
      type: UPDATE_DARK_MODE,
      payload: { dark: override ? __getDarkModePreference__() : Device.darkMode }
    });

    dispatch(__setupColorSchemeMonitor__());
  }, 0);

  return (state = $state);
}
